import React, { useState, useEffect } from 'react';

const ManualDeviceRegistration = ({ isOpen, onClose, companyId, onSuccess }) => {
  const [macAddress, setMacAddress] = useState('');
  const [deviceName, setDeviceName] = useState('iClean Reader');
  const [error, setError] = useState('');
  const [registering, setRegistering] = useState(false);
  const [success, setSuccess] = useState(false);

  // Cleanup on close
  useEffect(() => {
    if (!isOpen) {
      setMacAddress('');
      setDeviceName('iClean Reader');
      setError('');
      setRegistering(false);
      setSuccess(false);
    }
  }, [isOpen]);

  const handleRegister = async () => {
    console.log('[REGISTER] Starting registration...', { macAddress, deviceName, companyId });
    
    if (!macAddress.trim()) {
      console.log('[REGISTER] Error: MAC address empty');
      setError('Please enter the device MAC address!');
      return;
    }

    // Validate MAC address format (basic check)
    const macRegex = /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/;
    if (!macRegex.test(macAddress)) {
      console.log('[REGISTER] Error: Invalid MAC format', macAddress);
      setError('Invalid MAC address format! (e.g. 74:78:57:1C:B2:B0)');
      return;
    }

    if (!deviceName.trim()) {
      console.log('[REGISTER] Error: Device name empty');
      setError('Please enter the device name!');
      return;
    }

    if (!companyId) {
      console.log('[REGISTER] Error: No company ID provided');
      setError('No company selected!');
      return;
    }

    console.log('[REGISTER] Validation passed');
    setRegistering(true);
    setError('');

    try {
      console.log('[REGISTER] Sending request to /api/device/register-manual');
      const response = await fetch('/api/device/register-manual', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          device_id: macAddress,
          device_name: deviceName,
          company_id: companyId
        })
      });

      console.log('[REGISTER] Response status:', response.status);
      
      if (!response.ok) {
        const data = await response.json();
        console.log('[REGISTER] Error response:', data);
        throw new Error(data.detail || 'Registration failed');
      }

      const responseData = await response.json();
      console.log('[REGISTER] Success response:', responseData);

      // Success
      setSuccess(true);
      
      // Callback to refresh device list
      if (onSuccess) {
        console.log('[REGISTER] Calling onSuccess callback');
        onSuccess();
      }
      
    } catch (err) {
      console.log('[REGISTER] Exception:', err);
      setError(err.message);
      setRegistering(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="bg-blue-600 text-white p-4 rounded-t-lg flex justify-between items-center">
          <h2 className="text-lg font-semibold">New Device Registration</h2>
          <button
            onClick={onClose}
            className="text-white hover:bg-blue-700 rounded-full w-8 h-8 flex items-center justify-center"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {success ? (
            // Success message
            <div className="text-center">
              <div className="mb-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Device Successfully Registered!
                </h3>
                <div className="bg-gray-100 p-3 rounded mb-4">
                  <p className="text-sm text-gray-700 font-mono">
                    {macAddress}
                  </p>
                  <p className="text-sm text-gray-700 font-medium mt-1">
                    {deviceName}
                  </p>
                </div>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded p-4 mb-4 text-left">
                <p className="text-sm text-gray-700 mb-2">
                  <strong>Next steps:</strong>
                </p>
                <ol className="text-sm text-gray-700 space-y-2 list-decimal list-inside">
                  <li>Power on your device</li>
                  <li>Check the <strong>Devices</strong> panel to see when it connects</li>
                  <li>The device will appear with online status when connected</li>
                </ol>
              </div>

              <button
                onClick={onClose}
                className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700 font-medium"
              >
                Close
              </button>
            </div>
          ) : (
            // Registration form
            <>
              <div className="mb-4">
                <label className="block text-gray-700 font-medium mb-2">
                  Device MAC Address:
                </label>
                <input
                  type="text"
                  value={macAddress}
                  onChange={(e) => setMacAddress(e.target.value.toUpperCase())}
                  placeholder="e.g. 74:78:57:1C:B2:B0"
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                  disabled={registering}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Find the MAC address on the back of your device.
                </p>
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 font-medium mb-2">
                  Device Name:
                </label>
                <input
                  type="text"
                  value={deviceName}
                  onChange={(e) => setDeviceName(e.target.value)}
                  placeholder="e.g. Reader #1"
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={registering}
                />
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded text-sm">
                  {error}
                </div>
              )}

              <button
                onClick={handleRegister}
                disabled={registering}
                className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700 font-medium disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {registering ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Registering...
                  </>
                ) : (
                  'Register Device'
                )}
              </button>

              <p className="text-xs text-gray-500 mt-4 text-center">
                After registration, power on your device to activate it.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManualDeviceRegistration;

